import React from 'react';
import {
  Button,
  Badge,
  Box,
  Flex,
  Switch,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tab,
  Text,
  Divider,
} from 'pounceV2';
import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import { useNotifications } from 'Components/Notifications/graphql/Notifications.generated';
import useInfiniteScroll from 'Hooks/useInfiniteScroll';
import { useTheme } from '@mui/material';
import { groupedNotifications } from './utils';
import NotificationsPerDateV3 from './NotificationsPerDateV3';
import NotificationsHeaderV3 from './NotificationsHeaderV3';
import NotificationsErrorV3 from './NotificationsErrorV3';
import NotificationsEmptyStateV3 from './NotificationsEmptyStateV3';
import NotificationsActionsV3 from './NotificationsActionsV3';

const NotificationsV3 = ({ onToggleSettings }: { onToggleSettings: (flag: boolean) => void }) => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const [showRead, setShowRead] = React.useState(false);
  const {
    loading,
    error,
    previousData,
    data = previousData,
    fetchMore,
  } = useNotifications({
    variables: { input: { pageSize: 500 } },
  });
  const theme = useTheme();

  const endCursor = data?.notifications?.pageInfo.endCursor ?? null;
  const hasNextPage = data?.notifications?.pageInfo.hasNextPage ?? false;
  const notifications = React.useMemo(
    () => data?.notifications.edges.map(edge => edge.node) ?? [],
    [data?.notifications.edges],
  );

  const { inboxCount, archivedCount, unReadCount, groupedInbox, groupedArchived } =
    React.useMemo(() => {
      // Sort notifications by createdAt date
      const sortedNotifications = notifications.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      // seperate archived and unarchived notifications
      const { archived, unarchived } = sortedNotifications.reduce(
        (acc, notification) => {
          if (Date.parse(notification.archivedAt) >= 0) {
            acc.archived.push(notification);
          } else {
            acc.unarchived.push(notification);
          }
          return acc;
        },
        { archived: [], unarchived: [] },
      );

      // count unread notifications
      const unreadNotificationsCount = unarchived.filter(
        notification => Date.parse(notification.readAt) < 0,
      ).length;

      const unarchivedOrRead = showRead
        ? unarchived
        : unarchived.filter(n => Date.parse(n.readAt) < 0);

      return {
        inboxCount: unarchivedOrRead.length,
        archivedCount: archived.length,
        unReadCount: unreadNotificationsCount,
        groupedInbox: groupedNotifications(unarchivedOrRead),
        groupedArchived: groupedNotifications(archived),
      };
    }, [notifications, showRead]);

  const { sentinelRef } = useInfiniteScroll<HTMLDivElement>({
    loading,
    onLoadMore: () => {
      fetchMore({
        variables: {
          input: {
            cursor: endCursor,
            pageSize: 100,
          },
        },
      });
    },
  });

  if (loading && !data) {
    return null;
  }

  return (
    <ErrorBoundaryV3>
      <Box overflowY="auto" mb={2} height="calc(100% - 100px)" position="relative">
        <NotificationsHeaderV3 onToggleSettings={onToggleSettings} />
        <NotificationsErrorV3 error={error} />

        <Tabs variant="underline" index={tabIndex} onChange={setTabIndex}>
          <Flex px={6} align="baseline">
            <TabList data-dd-privacy="allow">
              <Tab>
                <Flex align="center">
                  <Text mr={2}>Inbox</Text>
                  <Box>
                    <Badge
                      emphasized
                      size="small"
                      color={
                        theme.palette.componentTokens.indicators.colors.neutral.content.text.resting
                      }
                      backgroundColor={
                        unReadCount > 0
                          ? theme.palette.componentTokens.indicators.colors.alert.background.resting
                          : theme.palette.componentTokens.indicators.colors.neutral.background
                              .resting
                      }
                    >
                      {unReadCount > 99 ? '99+' : unReadCount.toLocaleString()}
                    </Badge>
                  </Box>
                </Flex>
              </Tab>
              <Tab>Archived</Tab>
            </TabList>

            {tabIndex === 0 && (
              <Box ml="auto">
                <Switch
                  checked={showRead}
                  onChange={e => setShowRead(e.target.checked)}
                  label="Show Read Notifications"
                  checkedText=""
                  uncheckedText=""
                />
              </Box>
            )}
          </Flex>
          <Divider margin={0} mx={6} />
          <TabPanels>
            <TabPanel lazy unmountWhenInactive>
              <Box as="section">
                <NotificationsActionsV3
                  disableMarkAsRead={!unReadCount}
                  disableMarkAsUnread={!inboxCount}
                  disableArchive={!inboxCount}
                />

                {loading || !inboxCount ? (
                  <NotificationsEmptyStateV3 />
                ) : (
                  <>
                    <NotificationsPerDateV3 notificationsGroup={groupedInbox} />
                    {hasNextPage && (
                      <Box py={8} ref={sentinelRef} display="flex" justifyContent="center">
                        <Button variant="ghost">Load More...</Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </TabPanel>
            <TabPanel lazy unmountWhenInactive>
              <Box as="section">
                <NotificationsActionsV3 tab="archived" disableUnArchive={!archivedCount} />
                {loading || !archivedCount ? (
                  <NotificationsEmptyStateV3 />
                ) : (
                  <>
                    <NotificationsPerDateV3 notificationsGroup={groupedArchived} />
                    {hasNextPage && (
                      <Box py={8} ref={sentinelRef} display="flex" justifyContent="center">
                        <Button variant="ghost">Load More...</Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </ErrorBoundaryV3>
  );
};

export default NotificationsV3;
